import {useQuery, useQueries} from "@tanstack/react-query";
import {AxiosError, AxiosResponse} from "axios";
import {
  WithdrawableBalance,
  PendingBalance,
  PaginatedPayoutHistory,
  PaginationParams,
} from "../domain/types";
import {createPayoutRepository} from "../data/repositories";
import {createBankAccountRepository, BankAccount} from "../../bank-accounts";

// Type from payout.ts for connected bank response (legacy format)
type TBank = {
  id: number;
  userId: number;
  country_name: string;
  country_code: string;
  currency: string;
  bank_name: string;
  bank_code: string;
  account_number: string;
  account_name: string;
  status_id: string;
  statusName: string;
  branch_code: string;
  provider: string;
  createdAt: string;
  updatedAt: string;
};

type TConnectedBankApiResponse = {
  bank: TBank;
};

/**
 * Converts BankAccount domain entity to legacy TBank format.
 */
const toLegacyBankResponse = (b: BankAccount): TBank => ({
  id: b.id,
  userId: b.userId,
  country_name: b.countryName,
  country_code: b.countryCode,
  currency: b.currency,
  bank_name: b.bankName,
  bank_code: b.bankCode,
  account_number: b.accountNumber,
  account_name: b.accountName,
  status_id: b.statusId,
  statusName: b.status,
  branch_code: b.branchCode || "",
  provider: b.provider,
  createdAt: b.createdAt,
  updatedAt: b.updatedAt,
});

// ==================== Domain Hooks ====================

/**
 * Hook for fetching withdrawable balance.
 * Returns strongly-typed WithdrawableBalance.
 */
export const useWithdrawableBalance = () => {
  const repository = createPayoutRepository();

  return useQuery<WithdrawableBalance, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["account_balance"],
    queryFn: () => repository.getWithdrawableBalance(),
    refetchInterval: 300000, // 5 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for fetching pending balance.
 * Returns strongly-typed PendingBalance.
 */
export const usePendingBalance = () => {
  const repository = createPayoutRepository();

  return useQuery<PendingBalance, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["pending_payout"],
    queryFn: () => repository.getPendingBalance(),
    staleTime: Infinity,
  });
};

/**
 * Hook for fetching payout history with optional pagination.
 * Returns strongly-typed PaginatedPayoutHistory.
 */
export const usePayoutHistory = (params?: PaginationParams) => {
  const repository = createPayoutRepository();

  return useQuery<PaginatedPayoutHistory, AxiosError<{status: boolean; message: string}>>(
    {
      queryKey: ["payout_history", params],
      queryFn: () => repository.getPayoutHistory(params),
      refetchInterval: 300000, // 5 minutes
      refetchOnWindowFocus: true,
    }
  );
};

// ==================== Legacy-Compatible Hooks ====================

/**
 * Legacy-compatible hook for fetching account balance.
 * Returns AxiosResponse wrapper shape for backwards compatibility.
 *
 * @deprecated Use useWithdrawableBalance for new code.
 */
export const useFetchAccountBalance = () => {
  const repository = createPayoutRepository();

  return useQuery<
    AxiosResponse<{balance: number}, unknown>,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["account_balance"],
    queryFn: async () => {
      const result = await repository.getWithdrawableBalance();
      // Return in AxiosResponse shape for backwards compatibility
      return {
        data: {balance: result.balance},
        status: 200,
        statusText: "OK",
        headers: {},
        config: {} as AxiosResponse["config"],
      };
    },
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Legacy-compatible hook for fetching payout history.
 * Returns AxiosResponse wrapper with { requests: [] } shape for backwards compatibility.
 *
 * @deprecated Use usePayoutHistory for new code.
 */
export const useFetchPayoutHistory = () => {
  const repository = createPayoutRepository();

  return useQuery<
    AxiosResponse<
      {
        requests: {
          id: number;
          createdAt: Date;
          amount: number;
          fee: number;
          currency: string;
          statusName: string;
          reference: string;
          transactionType: "DEBIT" | "CREDIT";
        }[];
      },
      unknown
    >,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["payout_history"],
    queryFn: async () => {
      const result = await repository.getPayoutHistory();
      // Map to legacy format
      return {
        data: {
          requests: result.data.map((item) => ({
            id: item.id,
            createdAt: new Date(item.createdAt),
            amount: item.amount,
            fee: item.fee,
            currency: item.currency,
            statusName: item.statusName,
            reference: item.reference,
            transactionType: item.transactionType,
          })),
        },
        status: 200,
        statusText: "OK",
        headers: {},
        config: {} as AxiosResponse["config"],
      };
    },
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Legacy-compatible composite hook for withdrawal details page.
 * Fetches balance, pending payout, and connected bank in parallel.
 *
 * @deprecated Use individual hooks for new code.
 */
export const useWithdrawalDetails = (options: {connectBankUniqueIdentifier: string}) => {
  const payoutRepository = createPayoutRepository();
  const bankAccountRepository = createBankAccountRepository();

  return useQueries({
    queries: [
      {
        queryKey: ["account_balance"],
        queryFn: async function fetch(): Promise<{balance: number}> {
          const result = await payoutRepository.getWithdrawableBalance();
          return {balance: result.balance};
        },
        refetchInterval: 300000,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["pending_payout"],
        queryFn: async function fetch(): Promise<{pendingPayouts: number}> {
          const result = await payoutRepository.getPendingBalance();
          return {pendingPayouts: result.pendingBalance};
        },
        staleTime: Infinity,
      },
      {
        queryKey: ["connected_bank", {uniqueId: options.connectBankUniqueIdentifier}],
        queryFn: async function fetch(): Promise<TConnectedBankApiResponse> {
          const bankAccount = await bankAccountRepository.getConnectedBankAccount();
          if (!bankAccount) {
            throw new Error(
              "Sorry, You do not have a bank account saved! Please, create one and try again!"
            );
          }
          return {bank: toLegacyBankResponse(bankAccount)};
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    ],
  });
};
